// Global Instructions Rule Applied!
//
// Create or edit a library stage (public.pipeline_stages). Shared by the
// Pipeline Stages panel and the per-pipeline detail panel, so a stage is
// edited the same way wherever it is reached.
//
// Two things make this more than a form:
//
//   * A stage row is SHARED. Attaching it to a second pipeline reuses the same
//     row, so a rename or a status change lands in every pipeline listed in
//     `used_by`. The modal shows that list, and confirms before a decision-status
//     change on a stage that is in use.
//   * decision_status drives behaviour, not just display — it feeds the follower
//     membership predicate (a terminal status ends following) and the
//     cfg.app_config terminal-status lists. So the picker is filtered to the
//     status category matching the chosen pipeline type, mirroring the DB
//     trigger (trg_pipeline_stages_status_category) rather than relying on it:
//     the trigger is the backstop, this is the affordance.
//
// Type is locked once the stage is attached anywhere — flipping the pipeline
// kind under live pipeline_config rows has no sane migration path.
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Form, Input, Modal, Select, Typography } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useLookups } from '../../global_configs/hooks/useLookups.ts'
import type { StageLibraryModel } from '../../../state_manager/data_models/stage_library_model.ts'
import { createStage, updateStage } from '../services/pipelinesConfigService.ts'
import ColourPickerField from './ColourPickerField.tsx'

const { Text } = Typography

const token = (): string | null => getAccessToken(store.getState().auth)

/** 'Deal Origination' -> the 'Deal Origination Status' lookup group. */
export const STATUS_GROUP_FOR_TYPE_LABEL = (typeLabel?: string | null): string | null =>
  typeLabel ? `${typeLabel} Status` : null

interface StageEditModalProps {
  open: boolean
  /** null = create mode. */
  stage: StageLibraryModel | null
  /** Pre-selects the pipeline type in create mode (from a pipeline's detail panel). */
  defaultTypeId?: string | null
  onClose: () => void
  onSaved: (stageId: string) => void
}

export default function StageEditModal({
  open,
  stage,
  defaultTypeId,
  onClose,
  onSaved
}: StageEditModalProps): React.ReactElement {
  const isEdit = !!stage
  const { getLookupOptions } = useLookups()
  // Modal.useModal, not the static Modal.confirm: under React 19 the static
  // function is CALLED but renders nothing (antd v5 supports React 16-18 and
  // warns "Static function can not consume context"), so a confirm-gated save
  // becomes a dead button. The hook renders through the normal tree.
  const [modal, modalContextHolder] = Modal.useModal()

  const [name, setName] = useState('')
  const [typeId, setTypeId] = useState<string | null>(null)
  const [decisionStatus, setDecisionStatus] = useState<string | null>(null)
  const [colour, setColour] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName((stage?.pipeline_stages__name as string) || '')
    setTypeId((stage?.pipeline_stages__type as string) || defaultTypeId || null)
    setDecisionStatus((stage?.pipeline_stages__decision_status as string) || null)
    setColour((stage?.pipeline_stages__colour as string) || null)
  }, [open, stage, defaultTypeId])

  const typeOptions = useMemo(() => getLookupOptions('Pipeline Type'), [getLookupOptions])

  const typeLabel = useMemo(
    () => typeOptions.find((o: any) => o.value === typeId)?.label ?? null,
    [typeOptions, typeId]
  )

  // Statuses for the chosen type only. An unrecognised type (a pipeline kind
  // with no status group yet) falls back to every group rather than an empty
  // picker, matching the trigger's "not a known category -> allow" posture.
  const decisionOptions = useMemo(() => {
    const group = STATUS_GROUP_FOR_TYPE_LABEL(typeLabel)
    const scoped = group ? getLookupOptions(group) : []
    if (scoped.length > 0) return scoped
    return ['Deal Origination Status', 'Deal Execution Status', 'Fundraising Status'].flatMap((g) =>
      getLookupOptions(g)
    )
  }, [getLookupOptions, typeLabel])

  // Changing the type can strand a status from the previous category; drop it
  // so the user cannot submit a pair the trigger will reject.
  useEffect(() => {
    if (!decisionStatus) return
    if (decisionOptions.length === 0) return
    if (!decisionOptions.some((o: any) => o.value === decisionStatus)) setDecisionStatus(null)
  }, [decisionOptions, decisionStatus])

  const usedBy = (stage?.pipeline_stages__used_by as { pipelines__name?: string | null }[]) || []
  const usageCount = (stage?.pipeline_stages__usage_count as number) ?? 0
  const dealCount = (stage?.pipeline_stages__deal_count as number) ?? 0
  const statusChanged = isEdit && decisionStatus !== (stage?.pipeline_stages__decision_status || null)

  const save = async (): Promise<void> => {
    setSaving(true)
    try {
      const fields: Record<string, unknown> = {
        name: name.trim(),
        type: typeId,
        decision_status: decisionStatus,
        colour
      }
      const result = isEdit
        ? await updateStage(stage!.pipeline_stages__id as string, fields, token())
        : await createStage(fields, token())
      if (result.error) {
        showErrorToast(result.error)
        return
      }
      toast.success(isEdit ? 'Stage updated' : 'Stage created')
      const created = (result.data as { id?: string } | undefined)?.id
      onSaved(created || (stage?.pipeline_stages__id as string) || '')
      onClose()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to save stage')
    } finally {
      setSaving(false)
    }
  }

  const handleOk = (): void => {
    if (!name.trim()) {
      showErrorToast('Stage name is required')
      return
    }
    if (!typeId) {
      showErrorToast('Pipeline type is required')
      return
    }
    if (!decisionStatus) {
      showErrorToast('Decision status is required')
      return
    }
    // A status change on a shared, in-use stage changes follower eligibility
    // and status reporting for every deal on it — say so before it happens.
    if (statusChanged && usageCount > 0) {
      modal.confirm({
        title: 'Change the decision status of a stage in use?',
        content: (
          <div className='space-y-2'>
            <div>
              This stage is used by {usageCount} pipeline{usageCount === 1 ? '' : 's'}
              {usedBy.length > 0 ? `: ${usedBy.map((p) => p.pipelines__name).join(', ')}` : ''}.
            </div>
            <div>
              {dealCount} deal{dealCount === 1 ? '' : 's'} currently sit{dealCount === 1 ? 's' : ''} on
              it. Follower eligibility and status reporting change immediately for those deals.
            </div>
          </div>
        ),
        okText: 'Change status',
        cancelText: 'Cancel',
        onOk: save
      })
      return
    }
    save()
  }

  return (
    <>
      {modalContextHolder}
      <Modal
      open={open}
      title={isEdit ? 'Edit stage' : 'New stage'}
      okText={isEdit ? 'Save' : 'Create'}
      confirmLoading={saving}
      onOk={handleOk}
      onCancel={onClose}
      destroyOnClose
    >
      <Form layout='vertical'>
        <Form.Item label='Name' required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g. Diligence & Rank'
            maxLength={120}
          />
        </Form.Item>

        <Form.Item
          label='Pipeline type'
          required
          extra={
            isEdit && usageCount > 0
              ? 'Locked — the stage is attached to a pipeline. Detach it everywhere to change its type.'
              : undefined
          }
        >
          <Select
            value={typeId ?? undefined}
            onChange={(v) => setTypeId(v)}
            options={typeOptions}
            disabled={isEdit && usageCount > 0}
            placeholder='Select a pipeline type'
          />
        </Form.Item>

        <Form.Item
          label='Decision status'
          required
          extra={typeLabel ? `Showing ${typeLabel} statuses` : 'Choose a pipeline type first'}
        >
          <Select
            value={decisionStatus ?? undefined}
            onChange={(v) => setDecisionStatus(v)}
            options={decisionOptions}
            placeholder='Select a decision status'
          />
        </Form.Item>

        <Form.Item label='Colour'>
          <ColourPickerField value={colour} onChange={setColour} />
        </Form.Item>

        {isEdit && usedBy.length > 1 && (
          <Alert
            type='warning'
            showIcon
            message='This stage is shared'
            description={`Changes apply everywhere it is used: ${usedBy
              .map((p) => p.pipelines__name)
              .join(', ')}.`}
          />
        )}
        {isEdit && usedBy.length === 1 && (
          <Text type='secondary'>Used by {usedBy[0].pipelines__name}.</Text>
        )}
      </Form>
      </Modal>
    </>
  )
}
